import { useAtom } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import type { Logos } from '@search-filters/Components/MakeLabel/Logos';
import MakeLabel from '@search-filters/Components/MakeLabel/MakeLabel';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink';
import { useSearchFilter } from '@search-filters/Hooks/UseSearchFilter';

import { FilterLink } from '../FilterLink/FilterLink';
import { POPULAR_MAKES_UK } from './MakeFilter.constants';

const filterId: keyof FilterParams = 'make';

function useMakeFilter(
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
  applyButton?: boolean,
) {
  const [{ makes }] = useAtom(aggregationsAtom);
  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  const allMakes = makes.map(make => {
    const maybeLink = getLinkForFilterValue({
      filterValue: make.key,
      resultCount: make.count ?? 0,
    });

    return {
      id: make.key,
      renderLabel: (
        <MakeLabel
          count={make.count}
          make={make.make as Logos}
          name={make.displayName}
          nameRender={<FilterLink link={maybeLink}>{make.displayName}</FilterLink>}
        />
      ),
      label: make.displayName,
    };
  });

  const { allOptions, ...rest } = useSearchFilter({
    filterId,
    data: allMakes,
    applyButton,
  });

  const POPULAR_MAKES = POPULAR_MAKES_UK;
  const popularMakes = allOptions.filter(make => POPULAR_MAKES.includes(make.id));

  return {
    popularMakes,
    allMakes: allOptions,
    ...rest,
  };
}

export default useMakeFilter;
